<template>
  <div class="demo-host-page">
    <iframe
      ref="demoFrame"
      class="demo-frame"
      title="客户管理确认版Demo"
      :src="demoUrl"
      frameborder="0"
      @load="onDemoLoad"
    />

    <a-modal
      :title="categoryModalMode === 'edit' ? '编辑客户类型' : '新建客户类型'"
      :visible="categoryModalVisible"
      :confirm-loading="categorySaving"
      ok-text="保存"
      cancel-text="取消"
      width="720px"
      wrap-class-name="archive-op-modal"
      destroy-on-close
      @ok="submitCategory"
      @cancel="categoryModalVisible = false"
    >
      <div class="archive-op">
        <div class="archive-op-summary">
          <div><span>功能模块</span><b>客户管理 / 客户档案</b></div>
          <div><span>当前操作</span><b>{{ categoryModalMode === 'edit' ? '编辑客户类型' : '新建客户类型' }}</b></div>
          <div><span>操作对象</span><b>{{ categoryModalMode === 'edit' ? (categoryForm.categoryName || '未选择') : categoryForm.parentName }}</b></div>
        </div>
        <div class="archive-op-section">
          <div class="archive-op-title">类型信息</div>
          <div class="archive-op-body">
            <div class="archive-op-grid">
              <label v-if="categoryModalMode === 'create'" class="archive-op-field">
                <span>上级类型</span>
                <a-input :value="categoryForm.parentName" disabled />
              </label>
              <label class="archive-op-field">
                <span>类型名称</span>
                <a-input v-model="categoryForm.categoryName" placeholder="请输入类型名称" />
              </label>
              <label class="archive-op-field">
                <span>类型编码</span>
                <a-input v-model="categoryForm.categoryCode" placeholder="系统自动生成" :disabled="categoryModalMode === 'edit'" />
              </label>
              <label v-if="categoryModalMode === 'edit'" class="archive-op-field">
                <span>客户数量</span>
                <a-input :value="String(categoryForm.customerCount == null ? 0 : categoryForm.customerCount)" disabled />
              </label>
              <label class="archive-op-field">
                <span>排序号</span>
                <a-input-number v-model="categoryForm.sortNo" :min="0" :precision="0" style="width:100%" />
              </label>
              <label class="archive-op-field">
                <span>状态</span>
                <a-select v-model="categoryForm.status">
                  <a-select-option value="启用">启用</a-select-option>
                  <a-select-option value="停用">停用</a-select-option>
                </a-select>
              </label>
              <label class="archive-op-field">
                <span>匹配客商类型</span>
                <a-input v-model="categoryForm.matchCustomerType" placeholder="可选" />
              </label>
              <label class="archive-op-field archive-op-field-full">
                <span>备注</span>
                <a-textarea v-model="categoryForm.remark" :rows="3" placeholder="请输入类型说明" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import storage from 'store'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import { getToken } from '@/utils/auth'
import {
  treeCustomerCategory,
  addCustomerCategory,
  editCustomerCategory,
  enableCustomerCategory,
  disableCustomerCategory,
  deleteCustomerCategory
} from '@/api/erp/customerCategory'

function flattenTree (nodes, parentId, acc) {
  const list = acc || []
  ;(nodes || []).forEach(node => {
    list.push({
      id: node.id,
      parentId: node.parentId != null ? node.parentId : parentId,
      categoryCode: node.categoryCode,
      categoryName: node.categoryName,
      sortNo: node.sortNo,
      matchCustomerType: node.matchCustomerType,
      status: node.status,
      customerCount: node.customerCount,
      children: node.children || []
    })
    if (node.children && node.children.length) {
      flattenTree(node.children, node.id, list)
    }
  })
  return list
}

export default {
  name: 'ErpCustomer',
  data () {
    return {
      // 固定版本号，避免每次进页 Date.now() 强制重载整页大 Demo（解析慢）
      demoUrl: '/dj-demo/customer_management.html?v=20261024-first-order-filter',
      categoryModalVisible: false,
      categoryModalMode: 'create',
      categorySaving: false,
      categoryTree: [],
      categoryForm: {
        id: undefined,
        parentId: 0,
        parentName: '全部客户',
        categoryName: '',
        categoryCode: '',
        sortNo: 10,
        status: '启用',
        originStatus: '启用',
        matchCustomerType: '',
        remark: '',
        customerCount: 0
      }
    }
  },
  methods: {
    resolveToken () {
      return storage.get(ACCESS_TOKEN) || getToken() || ''
    },
    pushTokenToFrame () {
      const token = this.resolveToken()
      if (!token) return
      try {
        sessionStorage.setItem('DJ_AUTH_TOKEN', token)
        localStorage.setItem('DJ_AUTH_TOKEN', token)
      } catch (e) { /* ignore */ }
      const frame = this.$refs.demoFrame
      if (!frame || !frame.contentWindow) return
      frame.contentWindow.postMessage({ type: 'DJ_AUTH', token }, window.location.origin)
    },
    resolveThemeTokens () {
      if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return {}
      const styles = window.getComputedStyle(document.documentElement)
      const tokenMap = {
        primary: '--bd-primary',
        primaryHover: '--bd-primary-hover',
        primaryActive: '--bd-primary-active',
        primarySoft: '--bd-primary-soft',
        pageBg: '--bd-page-bg',
        surface: '--bd-surface',
        surfaceSubtle: '--bd-surface-subtle',
        border: '--bd-border',
        borderStrong: '--bd-border-strong',
        text: '--bd-text',
        textSecondary: '--bd-text-secondary',
        textMuted: '--bd-text-muted',
        success: '--bd-success',
        warning: '--bd-warning',
        danger: '--bd-danger',
        dangerSoft: '--bd-danger-soft',
        radiusControl: '--bd-radius-control',
        radiusCard: '--bd-radius-card',
        shadowCard: '--bd-shadow-card',
        shadowFloat: '--bd-shadow-float'
      }
      return Object.keys(tokenMap).reduce((result, key) => {
        const value = styles.getPropertyValue(tokenMap[key]).trim()
        if (value) result[key] = value
        return result
      }, {})
    },
    pushThemeToFrame () {
      const frame = this.$refs.demoFrame
      if (!frame || !frame.contentWindow) return
      const appState = (this.$store && this.$store.state && this.$store.state.app) || {}
      const profile = appState.uiProfile || 'legacy'
      const theme = appState.uiTheme || 'meteor-blue'
      frame.contentWindow.postMessage({
        type: 'DJ_THEME',
        profile,
        theme,
        tokens: profile === 'baidao' ? this.resolveThemeTokens() : {}
      }, window.location.origin)
    },
    pushFrameContext () {
      this.pushTokenToFrame()
      this.pushThemeToFrame()
    },
    onDemoLoad () {
      this.pushFrameContext()
      setTimeout(() => this.pushFrameContext(), 200)
      setTimeout(() => this.pushFrameContext(), 800)
    },
    refreshBaseOptions (dictType) {
      const frame = this.$refs.demoFrame
      if (!frame || !frame.contentWindow) return
      const reload = frame.contentWindow.__DJ_CUSTOMER_DICT_RELOAD__
      if (typeof reload !== 'function') return
      Promise.resolve(reload(dictType)).catch(() => {
        this.$message.error('客户基础选项刷新失败，请稍后重试')
      })
    },
    onBaseOptionChanged (event) {
      const detail = event && event.detail
      const dictType = detail && detail.dictType
      if (!dictType || !this.customerOptionTypes.includes(dictType)) return
      this.refreshBaseOptions(dictType)
    },
    notifyFrameCategoryReload () {
      const frame = this.$refs.demoFrame
      if (!frame || !frame.contentWindow) return
      frame.contentWindow.postMessage({ type: 'DJ_CUSTOMER_CATEGORY_RELOAD' }, window.location.origin)
    },
    nextCategoryCode () {
      return 'CC' + Date.now().toString(36).toUpperCase()
    },
    isAllCategory (node) {
      if (!node) return true
      return node.categoryCode === 'all' || node.categoryName === '全部客户'
    },
    findCategoryById (id) {
      if (id == null || id === '') return null
      const flat = flattenTree(this.categoryTree, 0)
      return flat.find(item => String(item.id) === String(id)) || null
    },
    categoryLabel (id) {
      if (id == null || id === 0 || id === '0') return '全部客户'
      const node = this.findCategoryById(id)
      return node ? (node.categoryName || '未命名') : '全部客户'
    },
    async loadCategoryTree () {
      const res = await treeCustomerCategory()
      this.categoryTree = (res && res.data) || []
      return this.categoryTree
    },
    async openCategoryModal (mode, selectedId) {
      try {
        await this.loadCategoryTree()
      } catch (e) {
        this.$message.error('加载客户类型目录失败')
        return
      }
      const selected = this.findCategoryById(selectedId)
      if (mode === 'edit') {
        if (!selected || this.isAllCategory(selected)) {
          this.$message.warning('请先选择要编辑的客户类型')
          return
        }
        const status = selected.status === 'disabled' || selected.status === '停用' ? '停用' : '启用'
        this.categoryModalMode = 'edit'
        this.categoryForm = {
          id: selected.id,
          parentId: selected.parentId || 0,
          parentName: this.categoryLabel(selected.parentId),
          categoryName: selected.categoryName || '',
          categoryCode: selected.categoryCode || '',
          sortNo: selected.sortNo == null ? 10 : selected.sortNo,
          status,
          originStatus: status,
          matchCustomerType: selected.matchCustomerType || '',
          remark: selected.remark || '',
          customerCount: selected.customerCount == null ? 0 : selected.customerCount
        }
      } else {
        const parent = selected && !this.isAllCategory(selected) ? selected : null
        this.categoryModalMode = 'create'
        this.categoryForm = {
          id: undefined,
          parentId: parent ? parent.id : 0,
          parentName: parent ? this.categoryLabel(parent.id) : '全部客户',
          categoryName: '',
          categoryCode: '',
          sortNo: 10,
          status: '启用',
          originStatus: '启用',
          matchCustomerType: '',
          remark: '',
          customerCount: 0
        }
      }
      this.categoryModalVisible = true
    },
    async submitCategory () {
      const name = (this.categoryForm.categoryName || '').trim()
      if (!name) {
        this.$message.warning('请输入类型名称')
        return
      }
      const payload = {
        categoryCode: (this.categoryForm.categoryCode || '').trim() || this.nextCategoryCode(),
        categoryName: name,
        parentId: this.categoryForm.parentId || 0,
        sortNo: this.categoryForm.sortNo == null ? 10 : Number(this.categoryForm.sortNo),
        matchCustomerType: (this.categoryForm.matchCustomerType || '').trim(),
        remark: this.categoryForm.remark || ''
      }
      if (this.categoryModalMode === 'edit') payload.id = this.categoryForm.id
      this.categorySaving = true
      try {
        const res = this.categoryModalMode === 'edit'
          ? await editCustomerCategory(payload)
          : await addCustomerCategory(payload)
        const id = (res && res.data && res.data.id) || this.categoryForm.id
        const wantDisabled = this.categoryForm.status === '停用'
        const wasDisabled = this.categoryForm.originStatus === '停用'
        if (id && wantDisabled !== wasDisabled) {
          if (wantDisabled) await disableCustomerCategory(id)
          else await enableCustomerCategory(id)
        }
        this.$message.success(this.categoryModalMode === 'edit' ? '客户类型已保存' : '客户类型已新建')
        this.categoryModalVisible = false
        this.notifyFrameCategoryReload()
      } catch (e) {
        // 接口错误由请求拦截器提示
      } finally {
        this.categorySaving = false
      }
    },
    async removeCategory (selectedId) {
      try {
        await this.loadCategoryTree()
      } catch (e) {
        this.$message.error('加载客户类型目录失败')
        return
      }
      const selected = this.findCategoryById(selectedId)
      if (!selected || this.isAllCategory(selected)) {
        this.$message.warning('请先选择要删除的客户类型')
        return
      }
      const name = selected.categoryName || '该类型'
      const count = Number(selected.customerCount || 0) || 0
      if (count > 0) {
        this.$message.warning(`「${name}」下还有 ${count} 个客户，不能删除`)
        return
      }
      if ((selected.children || []).length) {
        this.$message.warning(`「${name}」下还有子类型，请先删除子类型`)
        return
      }
      this.$confirm({
        title: '删除客户类型',
        content: `确认删除类型「${name}」？删除后不可恢复。`,
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => deleteCustomerCategory(selected.id).then(() => {
          this.$message.success('类型已删除')
          this.notifyFrameCategoryReload()
        })
      })
    },
    onFrameMessage (ev) {
      const frame = this.$refs.demoFrame
      if (ev.origin !== window.location.origin || (frame && frame.contentWindow && ev.source !== frame.contentWindow)) return
      const data = ev.data || {}
      if (data.type === 'DJ_THEME_READY') {
        this.pushThemeToFrame()
        return
      }
      if (data.type === 'DJ_CUSTOMER_CATEGORY_MODAL') {
        this.openCategoryModal(data.mode === 'edit' ? 'edit' : 'create', data.selectedId)
        return
      }
      if (data.type === 'DJ_CUSTOMER_CATEGORY_DELETE') {
        this.removeCategory(data.selectedId)
        return
      }
      if (data && data.type === 'DJ_GOTO' && data.path) {
        // 客户类型目录改为弹窗，不再跳转到独立维护页
        if (String(data.path).indexOf('customer-category') >= 0 || String(data.path).indexOf('customerCategory') >= 0) {
          this.openCategoryModal('create')
          return
        }
        this.$router.push({ path: data.path }).catch(() => {})
      }
    }
  },
  mounted () {
    this.pushFrameContext()
    window.addEventListener('message', this.onFrameMessage)
    window.addEventListener('erp-basic-option-changed', this.onBaseOptionChanged)
  },
  activated () {
    this.pushFrameContext()
    this.refreshBaseOptions()
  },
  beforeDestroy () {
    window.removeEventListener('message', this.onFrameMessage)
    window.removeEventListener('erp-basic-option-changed', this.onBaseOptionChanged)
  },
  computed: {
    customerOptionTypes () {
      return [
        'erp_customer_cooperation',
        'erp_customer_kind',
        'erp_customer_merchant_type',
        'erp_customer_channel',
        'erp_company_nature',
        'erp_customer_level',
        'erp_customer_intention',
        'erp_customer_business_status',
        'erp_delivery_method',
        'erp_settlement_method'
      ]
    }
  },
  watch: {
    '$store.state.app.uiProfile' () {
      this.$nextTick(() => this.pushThemeToFrame())
    },
    '$store.state.app.uiTheme' () {
      this.$nextTick(() => this.pushThemeToFrame())
    }
  }
}
</script>

<style scoped>
.demo-host-page {
  height: calc(100vh - 120px);
  min-height: 760px;
  margin: -24px;
  background: var(--bd-page-bg, #f5f7fa);
}

.demo-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: var(--bd-page-bg, #f5f7fa);
}

.archive-op{display:flex;flex-direction:column;gap:12px}
.archive-op-summary{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border:1px solid #e4e9f0;border-radius:6px;background:#f8fafc;overflow:hidden}
.archive-op-summary>div{min-width:0;padding:10px 12px;border-right:1px solid #e4e9f0}
.archive-op-summary>div:last-child{border-right:0}
.archive-op-summary span{display:block;margin-bottom:4px;color:#8a95a6;font-size:11px}
.archive-op-summary b{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#1d2129;font-size:13px;font-weight:600}
.archive-op-section{border:1px solid #e4e9f0;border-radius:6px;overflow:hidden}
.archive-op-title{padding:10px 13px;border-bottom:1px solid #edf0f4;background:#fafbfc;color:#1d2129;font-weight:600;font-size:13px}
.archive-op-body{padding:14px}
.archive-op-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px 18px}
.archive-op-field{display:flex;min-width:0;flex-direction:column;gap:6px;margin:0}
.archive-op-field-full{grid-column:1/-1}
.archive-op-field>span{color:#4e5969;font-size:12px}
.archive-op-field /deep/ .ant-input,
.archive-op-field /deep/ .ant-input-number,
.archive-op-field /deep/ .ant-select{width:100%}
.archive-op-field /deep/ .ant-input,
.archive-op-field /deep/ .ant-select-selection,
.archive-op-field /deep/ .ant-input-number-input{border-radius:4px}
</style>
